import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Avatar, Input, Select, Option, Modal, ModalDialog, ModalClose,
    IconButton, Tooltip,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon       from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon             from '@mui/icons-material/AddRounded';
import SearchRoundedIcon          from '@mui/icons-material/SearchRounded';
import EditRoundedIcon            from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon          from '@mui/icons-material/DeleteRounded';
import LockResetRoundedIcon       from '@mui/icons-material/LockResetRounded';
import BlockRoundedIcon           from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon     from '@mui/icons-material/CheckCircleRounded';
import LocationOnRoundedIcon      from '@mui/icons-material/LocationOnRounded';
import StorefrontRoundedIcon      from '@mui/icons-material/StorefrontRounded';
import EmailRoundedIcon           from '@mui/icons-material/EmailRounded';
import VisibilityRoundedIcon      from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon   from '@mui/icons-material/VisibilityOffRounded';
import SaveRoundedIcon            from '@mui/icons-material/SaveRounded';
import RefreshRoundedIcon         from '@mui/icons-material/RefreshRounded';
import FilterListRoundedIcon      from '@mui/icons-material/FilterListRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetEmployees, APIGetLocations,
    APICreateEmployee, APIUpdateEmployee, APIDeleteEmployee,
    APIResetEmployeePassword,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const getInitials = (acc) => {
    if (!acc) return '?';
    const f = acc.first_name?.[0] || '';
    const l = acc.last_name?.[0]  || '';
    return (f + l).toUpperCase() || acc.email?.[0]?.toUpperCase() || '?';
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white',
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    '&:hover': { bgcolor: 'var(--joy-palette-background-level3)' },
};

const selectListboxSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    '& .MuiOption-root': {
        color: 'var(--joy-palette-text-primary)',
        fontSize: 13,
        '&:hover': { bgcolor: 'rgba(162,38,226,0.12)' },
        '&[aria-selected="true"]': {
            bgcolor: 'rgba(162,38,226,0.2)',
            color: 'var(--joy-palette-primary-300)',
            fontWeight: 600,
        },
    },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.05 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

// ════════════════════════════════════════════════════════════════════
//  Модалка создания / редактирования
// ════════════════════════════════════════════════════════════════════
function WorkstationModal({ open, workstation, locations, onClose, onSubmit, processing, error }) {
    const isEdit = !!workstation;

    const [name,       setName]       = useState('');
    const [email,      setEmail]      = useState('');
    const [password,   setPassword]   = useState('');
    const [showPw,     setShowPw]     = useState(false);
    const [locationId, setLocationId] = useState(null);
    const [isBlocked,  setIsBlocked]  = useState(false);

    useEffect(() => {
        if (open) {
            setName(workstation?.first_name || '');
            setEmail(workstation?.email || '');
            setPassword('');
            setShowPw(false);
            setLocationId(workstation?.linked_location || null);
            setIsBlocked(workstation?.is_blocked || false);
        }
    }, [open, workstation]);

    const handleSubmit = () => {
        if (!name.trim()) return;
        if (!isEdit && (!email.trim() || password.length < 6)) return;

        if (isEdit) {
            onSubmit({
                first_name: name.trim(),
                linked_location_id: locationId || null,
                is_blocked: isBlocked,
            });
        } else {
            onSubmit({
                email: email.trim(),
                password,
                first_name: name.trim(),
                last_name: '',
                role: 'workstation',
                linked_location_id: locationId || null,
            });
        }
    };

    const canSave = isEdit
        ? !!name.trim()
        : !!name.trim() && !!email.trim() && password.length >= 6;

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-surface)',
                border: '1px solid var(--joy-palette-divider)',
                maxWidth: 460, width: '100%',
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                    {isEdit ? 'Редактировать рабочую точку' : 'Новая рабочая точка'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                    {isEdit
                        ? 'Изменить имя, локацию или статус'
                        : 'Аккаунт для компьютера на точке — с него админы начинают смену'}
                </Typography>

                {/* Название точки */}
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                    Название
                </Typography>
                <Input
                    size="sm" placeholder="Например: ТЦ Галерея, Парк, Студия №1"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ ...inputSx, mb: 2, width: '100%' }}
                />

                {/* Email (только при создании) */}
                {!isEdit && (
                    <>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                            Email для входа
                        </Typography>
                        <Input
                            size="sm" type="email" placeholder="workstation-1@studio.local"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-tertiary)' }} />}
                            sx={{ ...inputSx, mb: 2, width: '100%' }}
                        />

                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                            Пароль (мин. 6 символов)
                        </Typography>
                        <Input
                            size="sm" type={showPw ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            endDecorator={
                                <IconButton size="sm" variant="plain" onClick={() => setShowPw(p => !p)}>
                                    {showPw
                                        ? <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                                        : <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                                    }
                                </IconButton>
                            }
                            sx={{ ...inputSx, mb: 2, width: '100%' }}
                        />
                    </>
                )}

                {/* Локация */}
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                    Привязанная точка
                </Typography>
                <Select
                    size="sm" value={locationId || ''}
                    onChange={(_, v) => setLocationId(v || null)}
                    placeholder="Выберите локацию"
                    sx={{ ...inputSx, mb: 2, width: '100%' }}
                    slotProps={{
                        listbox: {
                            sx: {
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                '& .MuiOption-root': {
                                    color: 'var(--joy-palette-text-primary)',
                                    fontSize: 13,
                                    '&:hover': {
                                        bgcolor: 'rgba(162,38,226,0.12)',
                                        color: 'var(--joy-palette-text-primary)',
                                    },
                                    '&[aria-selected="true"]': {
                                        bgcolor: 'rgba(162,38,226,0.2)',
                                        color: 'var(--joy-palette-primary-300)',
                                        fontWeight: 600,
                                    },
                                },
                            },
                        },
                    }} 
                >
                    <Option value="">— Без привязки —</Option>
                    {locations.map(l => (
                        <Option key={l.id} value={l.id}>{l.name}</Option>
                    ))}
                </Select>
                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: -1, mb: 2 }}>
                    Локация определяет, к какой точке привязаны чеки, штрафы и смены админов
                </Typography>

                {/* Заблокирован (только при редактировании) */}
                {isEdit && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, py: 1.2, mb: 2,
                        borderRadius: 'var(--joy-radius-md)',
                        border: '1px solid var(--joy-palette-divider)',
                        bgcolor: isBlocked ? 'rgba(248,113,113,0.06)' : 'var(--joy-palette-background-level2)',
                    }}>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                {isBlocked ? 'Заблокирован' : 'Активен'}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                {isBlocked ? 'Не сможет войти в систему' : 'Может работать на точке'}
                            </Typography>
                        </Box>
                        <Button
                            size="sm"
                            variant="outlined"
                            color={isBlocked ? 'success' : 'danger'}
                            startDecorator={isBlocked
                                ? <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                                : <BlockRoundedIcon sx={{ fontSize: 14 }} />
                            }
                            onClick={() => setIsBlocked(p => !p)}
                            sx={{
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 11, fontWeight: 700,
                            }}
                        >
                            {isBlocked ? 'Разблокировать' : 'Заблокировать'}
                        </Button>
                    </Box>
                )}

                {error && (
                    <Box sx={{
                        px: 1.5, py: 1, mb: 2,
                        borderRadius: 'var(--joy-radius-md)',
                        bgcolor: 'rgba(248,113,113,0.08)',
                        border: '1px solid rgba(248,113,113,0.3)',
                    }}>
                        <Typography sx={{ fontSize: 12, color: '#f87171' }}>
                            {error}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="neutral" size="sm" onClick={onClose} sx={inputSx}>
                        Отмена
                    </Button>
                    <Button
                        size="sm" loading={processing}
                        startDecorator={<SaveRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleSubmit}
                        disabled={!canSave}
                        sx={{
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700,
                        }}
                    >
                        {isEdit ? 'Сохранить' : 'Создать'}
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  Модалка сброса пароля
// ════════════════════════════════════════════════════════════════════
function ResetPasswordModal({ open, workstation, onClose, onSubmit, processing, error }) {
    const [password, setPassword] = useState('');
    const [showPw,   setShowPw]   = useState(false);

    useEffect(() => {
        if (open) { setPassword(''); setShowPw(false); }
    }, [open]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-surface)',
                border: '1px solid var(--joy-palette-divider)',
                maxWidth: 400,
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                    Сменить пароль
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                    {workstation?.first_name} · {workstation?.email}
                </Typography>

                <Input
                    size="sm" type={showPw ? 'text' : 'password'}
                    placeholder="Новый пароль (мин. 6 символов)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    endDecorator={
                        <IconButton size="sm" variant="plain" onClick={() => setShowPw(p => !p)}>
                            {showPw
                                ? <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                                : <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                            }
                        </IconButton>
                    }
                    sx={{ ...inputSx, mb: 2, width: '100%' }}
                />

                {error && (
                    <Typography sx={{ fontSize: 12, color: '#f87171', mb: 2 }}>
                        {error}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="neutral" size="sm" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        size="sm" loading={processing}
                        onClick={() => onSubmit(password)}
                        disabled={password.length < 6}
                        sx={{
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700,
                        }}
                    >
                        Сменить
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  Карточка рабочей точки
// ════════════════════════════════════════════════════════════════════
function WorkstationCard({ ws, onEdit, onResetPw, onDelete, canUpdate, canResetPw, canDelete }) {
    const blocked = ws.is_blocked;

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5,
                borderRadius: 'var(--joy-radius-xl)',
                border: '1px solid var(--joy-palette-divider)',
                bgcolor: blocked ? 'rgba(248,113,113,0.03)' : 'var(--joy-palette-background-level1)',
                opacity: blocked ? 0.7 : 1,
                transition: 'all .15s',
                '&:hover': {
                    borderColor: blocked ? 'rgba(248,113,113,0.3)' : 'rgba(34,197,94,0.35)',
                    bgcolor: blocked ? 'rgba(248,113,113,0.05)' : 'var(--joy-palette-background-level2)',
                },
            }}>
                {/* Аватар */}
                <Avatar sx={{
                    width: 44, height: 44, fontSize: 15, fontWeight: 800,
                    bgcolor: blocked ? 'rgba(248,113,113,0.15)' : 'rgba(34,197,94,0.12)',
                    color:   blocked ? '#f87171' : '#22c55e',
                    border:  `1.5px solid ${blocked ? 'rgba(248,113,113,0.3)' : 'rgba(34,197,94,0.3)'}`,
                    flexShrink: 0,
                }}>
                    <StorefrontRoundedIcon sx={{ fontSize: 20 }} />
                </Avatar>

                {/* Инфо */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                            {ws.first_name || ws.email}
                        </Typography>
                        {blocked && (
                            <Chip size="sm" variant="soft" sx={{
                                fontSize: 10, fontWeight: 700,
                                bgcolor: 'rgba(248,113,113,0.10)', color: '#f87171',
                            }}>
                                Заблокирован
                            </Chip>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                        <EmailRoundedIcon sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }} />
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ws.email}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                        <LocationOnRoundedIcon sx={{
                            fontSize: 12,
                            color: ws.linked_location_name
                                ? 'var(--joy-palette-primary-400)'
                                : 'var(--joy-palette-text-tertiary)',
                        }} />
                        <Typography sx={{
                            fontSize: 11, fontWeight: ws.linked_location_name ? 700 : 400,
                            color: ws.linked_location_name
                                ? 'var(--joy-palette-primary-300)'
                                : 'var(--joy-palette-text-tertiary)',
                        }}>
                            {ws.linked_location_name || 'Локация не привязана'}
                        </Typography>
                    </Box>

                    {ws.last_login && (
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                            Последний вход: {fmtTime(ws.last_login)}
                        </Typography>
                    )}
                </Box>

                {/* Кнопки */}
                <Box sx={{ display: 'flex', gap: 0.3, flexShrink: 0 }}>
                    {canResetPw && (
                        <Tooltip title="Сменить пароль" size="sm">
                            <IconButton size="sm" variant="plain"
                                onClick={() => onResetPw(ws)}
                                sx={{ color: 'var(--joy-palette-text-tertiary)', '&:hover': { color: 'var(--joy-palette-primary-400)' } }}
                            >
                                <LockResetRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canUpdate && (
                        <Tooltip title="Редактировать" size="sm">
                            <IconButton size="sm" variant="plain"
                                onClick={() => onEdit(ws)}
                                sx={{ color: 'var(--joy-palette-text-tertiary)', '&:hover': { color: 'var(--joy-palette-primary-400)' } }}
                            >
                                <EditRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip title="Удалить" size="sm">
                            <IconButton size="sm" variant="plain"
                                onClick={() => onDelete(ws)}
                                sx={{ color: 'var(--joy-palette-text-tertiary)', '&:hover': { color: 'var(--joy-palette-danger-400)' } }}
                            >
                                <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}

// ════════════════════════════════════════════════════════════════════
//  WorkstationsManagePage
// ════════════════════════════════════════════════════════════════════
export default function WorkstationsManagePage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate  = hasPerm('employees.create');
    const canUpdate  = hasPerm('employees.update');
    const canDelete  = hasPerm('employees.delete');
    const canResetPw = hasPerm('employees.reset_password');

    // ── Данные ─────────────────────────────────────────────────────
    const [workstations, setWorkstations] = useState([]);
    const [locations,    setLocations]    = useState([]);
    const [loading,      setLoading]      = useState(true);

    // ── Фильтры ────────────────────────────────────────────────────
    const [search,         setSearch]         = useState('');
    const [filterLocation, setFilterLocation] = useState('');
    const [filterStatus,   setFilterStatus]   = useState(''); // '' | 'active' | 'blocked' | 'unlinked'

    // ── Модалки ────────────────────────────────────────────────────
    const [editModal,    setEditModal]    = useState({ open: false, ws: null });
    const [pwModal,      setPwModal]      = useState({ open: false, ws: null });
    const [deleteTarget, setDeleteTarget] = useState(null);

    // ── Async state ────────────────────────────────────────────────
    const [processing, setProcessing] = useState(false);
    const [formError,  setFormError]  = useState('');

    // ── Загрузка ───────────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        const [wsList, locList] = await Promise.all([
            APIGetEmployees(token, 'workstation'),
            APIGetLocations(token),
        ]);
        if (Array.isArray(wsList))   setWorkstations(wsList);
        if (Array.isArray(locList))  setLocations(locList);
        setLoading(false);
        return true;
    }, [token]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // ── Действия ───────────────────────────────────────────────────
    const handleSave = async (payload) => {
        setProcessing(true);
        setFormError('');
        const isEdit = !!editModal.ws;
        const res = isEdit
            ? await APIUpdateEmployee(token, editModal.ws.id, payload)
            : await APICreateEmployee(token, payload);
        if (res?.error) {
            setFormError(parseApiError(res.error, 'Не удалось сохранить'));
        } else {
            showToast(isEdit ? 'Точка обновлена' : 'Точка создана');
            setEditModal({ open: false, ws: null });
            loadAll();
        }
        setProcessing(false);
    };

    const handleResetPassword = async (newPassword) => {
        if (!pwModal.ws) return;
        setProcessing(true);
        setFormError('');
        const res = await APIResetEmployeePassword(token, pwModal.ws.id, newPassword);
        if (res?.error) {
            setFormError(parseApiError(res.error, 'Не удалось сменить пароль'));
        } else {
            showToast('Пароль обновлён');
            setPwModal({ open: false, ws: null });
        }
        setProcessing(false);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setProcessing(true);
        const res = await APIDeleteEmployee(token, deleteTarget.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить'), 'danger');
        } else {
            showToast('Точка удалена');
            setWorkstations(prev => prev.filter(w => w.id !== deleteTarget.id));
        }
        setProcessing(false);
        setDeleteTarget(null);
    };

    // ── Фильтрация ─────────────────────────────────────────────────
    const filtered = useMemo(() => {
        return workstations.filter(ws => {
            // Защита: на странице должны быть только workstations
            if (ws.role !== 'workstation') return false;

            const q = search.toLowerCase().trim();
            if (q) {
                const matched =
                    ws.first_name?.toLowerCase().includes(q) ||
                    ws.email?.toLowerCase().includes(q) ||
                    ws.linked_location_name?.toLowerCase().includes(q);
                if (!matched) return false;
            }

            if (filterLocation === '__unlinked__' && ws.linked_location) return false;
            if (filterLocation && filterLocation !== '__unlinked__'
                && String(ws.linked_location) !== String(filterLocation)) return false;

            if (filterStatus === 'active'   && ws.is_blocked) return false;
            if (filterStatus === 'blocked'  && !ws.is_blocked) return false;
            if (filterStatus === 'unlinked' && ws.linked_location) return false;

            return true;
        });
    }, [workstations, search, filterLocation, filterStatus]);

    // ── Статистика ─────────────────────────────────────────────────
    const stats = useMemo(() => {
        const total      = workstations.length;
        const active     = workstations.filter(w => !w.is_blocked).length;
        const blocked    = workstations.filter(w => w.is_blocked).length;
        const unlinked   = workstations.filter(w => !w.linked_location).length;
        const linkedLocs = new Set(workstations.filter(w => w.linked_location).map(w => w.linked_location)).size;
        return { total, active, blocked, unlinked, linkedLocs };
    }, [workstations]);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ────────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/manager')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Рабочие аккаунты
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    Управление аккаунтами компьютеров на точках
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                variant="outlined" color="neutral"
                                onClick={loadAll}
                                sx={{
                                    bgcolor: 'var(--joy-palette-background-level2)',
                                    border: '1px solid var(--joy-palette-divider)',
                                }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            {canCreate && (
                                <Button
                                    startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => { setFormError(''); setEditModal({ open: true, ws: null }); }}
                                    sx={{
                                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                        color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                        boxShadow: '0 4px 16px rgba(34,197,94,0.35)',
                                        '&:hover': { boxShadow: '0 6px 24px rgba(34,197,94,0.5)' },
                                    }}
                                >
                                    Новая точка
                                </Button>
                            )}
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Стат-карточки ─────────────────────────────────── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5, mb: 2.5 }}>
                    {[
                        {
                            label: 'Всего точек', value: stats.total,
                            color: '#22c55e', bg: 'rgba(34,197,94,0.08)',
                            icon: <StorefrontRoundedIcon sx={{ fontSize: 18 }} />,
                        },
                        {
                            label: 'Активные', value: stats.active,
                            color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)',
                            icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />,
                        },
                        {
                            label: 'Заблокированы', value: stats.blocked,
                            color: '#ef4444', bg: 'rgba(239,68,68,0.08)',
                            icon: <BlockRoundedIcon sx={{ fontSize: 18 }} />,
                        },
                        {
                            label: 'Без локации', value: stats.unlinked,
                            color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',
                            icon: <LocationOnRoundedIcon sx={{ fontSize: 18 }} />,
                        },
                    ].map(s => (
                        <Box key={s.label} sx={{
                            px: 2, py: 1.8,
                            borderRadius: 'var(--joy-radius-lg)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: s.bg,
                        }}>
                            <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                            <Typography sx={{ fontSize: 24, fontWeight: 800, color: s.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                {s.value}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                {s.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* ── Фильтры ──────────────────────────────────────── */}
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 1.5, sm: 2 }, mb: 2.5,
                }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Input
                            placeholder="Поиск по названию, email, локации…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />}
                            sx={{ ...inputSx, flex: 1 }} size="sm"
                        />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                        <Select size="sm" value={filterLocation}
                            onChange={(_, v) => setFilterLocation(v || '')}
                            placeholder="Все локации"
                            sx={inputSx}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }} 
                        >
                            <Option value="">Все локации</Option>
                            <Option value="__unlinked__">Без привязки</Option>
                            {locations.map(l => (
                                <Option key={l.id} value={String(l.id)}>{l.name}</Option>
                            ))}
                        </Select>
                        <Select size="sm" value={filterStatus}
                            onChange={(_, v) => setFilterStatus(v || '')}
                            placeholder="Все статусы"
                            sx={inputSx}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }} 
                        >
                            <Option value="">Все статусы</Option>
                            <Option value="active">Только активные</Option>
                            <Option value="blocked">Только заблокированные</Option>
                            <Option value="unlinked">Без локации</Option>
                        </Select>
                    </Box>
                </Box>

                {/* ── Список ───────────────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <StorefrontRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            {workstations.length === 0
                                ? 'Пока нет ни одной рабочей точки'
                                : 'Ничего не найдено по выбранным фильтрам'}
                        </Typography>
                        {workstations.length === 0 && canCreate && (
                            <Button
                                size="sm"
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => { setFormError(''); setEditModal({ open: true, ws: null }); }}
                                sx={{
                                    mt: 2,
                                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                    color: '#fff', fontWeight: 700,
                                }}
                            >
                                Создать первую
                            </Button>
                        )}
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1.2}>
                            {filtered.map(ws => (
                                <WorkstationCard
                                    key={ws.id}
                                    ws={ws}
                                    canUpdate={canUpdate}
                                    canResetPw={canResetPw}
                                    canDelete={canDelete}
                                    onEdit={(w) => { setFormError(''); setEditModal({ open: true, ws: w }); }}
                                    onResetPw={(w) => { setFormError(''); setPwModal({ open: true, ws: w }); }}
                                    onDelete={(w) => setDeleteTarget(w)}
                                />
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            {/* ── Модалки ──────────────────────────────────────────── */}
            <WorkstationModal
                open={editModal.open}
                workstation={editModal.ws}
                locations={locations}
                onClose={() => setEditModal({ open: false, ws: null })}
                onSubmit={handleSave}
                processing={processing}
                error={formError}
            />

            <ResetPasswordModal
                open={pwModal.open}
                workstation={pwModal.ws}
                onClose={() => setPwModal({ open: false, ws: null })}
                onSubmit={handleResetPassword}
                processing={processing}
                error={formError}
            />

            <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 400,
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Удалить рабочую точку?
                    </Typography>
                    {deleteTarget && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                            <strong>{deleteTarget.first_name || deleteTarget.email}</strong>
                            <br />
                            <span style={{ color: 'var(--joy-palette-text-tertiary)' }}>
                                {deleteTarget.email}
                                {deleteTarget.linked_location_name && ` · ${deleteTarget.linked_location_name}`}
                            </span>
                        </Typography>
                    )}
                    <Box sx={{
                        bgcolor: 'rgba(245,158,11,0.08)',
                        border: '1px solid rgba(245,158,11,0.3)',
                        borderRadius: 'var(--joy-radius-md)',
                        px: 1.5, py: 1, mb: 2,
                    }}>
                        <Typography sx={{ fontSize: 11, color: '#f59e0b' }}>
                            Все привязанные чеки, штрафы и смены сохранятся, но потеряют связь с этим аккаунтом.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="neutral" size="sm" onClick={() => setDeleteTarget(null)}>
                            Отмена
                        </Button>
                        <Button color="danger" size="sm" loading={processing} onClick={handleDelete}>
                            Удалить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}